import { deleteField, doc, updateDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { firebaseHelper } from "ui";
import {
  ProductInterface,
  validateProductLink,
  validateProductSku,
} from "ts-interfaces";
import { deepClone } from "../../libs/util";

// Whitelisted identity edits on the caller's own userProducts docs, shared by
// the schedule editor and My Library. Dot-path metadata mirrors only — an
// object-form `metadata` write would replace the clipper's metadata map.

export const PRODUCT_IDENTITY_FIELDS = [
  "name",
  "supplier",
  "sku",
  "productLink",
  "description",
  "refImages",
  "category",
  "finish",
] as const;
export type ProductIdentityField = (typeof PRODUCT_IDENTITY_FIELDS)[number];
// Loose: clipper product categories sit outside the material enums.
// `finish` is a paint's preset key, stored at materialData.preset.
export type ProductIdentityPatch = Partial<
  Pick<ProductInterface, Exclude<ProductIdentityField, "category" | "finish">>
> & { category?: string[]; finish?: string };

// Same mirror as the Material Details dialog ("Other" → null).
const mainCategoryFor = (category: string[]): string[] | null =>
  category[0] === "Other" ? null : category;

export function canEditUserProductIdentity(
  product: ProductInterface | undefined,
  uid: string | undefined | null,
): boolean {
  return !!uid && product?.uid === uid;
}

/** Validates and trims a patch; throws rather than dropping. `undefined` means delete. */
export function normalizeIdentityPatch(
  patch: ProductIdentityPatch,
): ProductIdentityPatch {
  const keys = Object.keys(patch) as ProductIdentityField[];
  if (keys.length === 0) throw new Error("Nothing to update");
  for (const key of keys) {
    if (!(PRODUCT_IDENTITY_FIELDS as readonly string[]).includes(key))
      throw new Error(`"${key}" is not an editable product field`);
  }
  const next: ProductIdentityPatch = { ...patch };
  if ("name" in next) {
    const name = next.name?.trim();
    if (!name) throw new Error("Product name is required");
    if (name.length >= 100)
      throw new Error("Product name must be less than 100 characters");
    next.name = name;
  }
  if (next.sku != null) validateProductSku(next.sku);
  if (next.productLink != null) validateProductLink(next.productLink);
  if (
    next.category !== undefined &&
    (!Array.isArray(next.category) ||
      next.category.length !== 1 ||
      typeof next.category[0] !== "string" ||
      !next.category[0].trim())
  )
    throw new Error("category must be a single category name");
  if ("finish" in next && (typeof next.finish !== "string" || !next.finish))
    throw new Error("finish must be a preset name");
  if ("refImages" in next && next.refImages !== undefined) {
    // Hosted https URLs only (upload first via uploadFileToGCloud) — data
    // URIs burn the 1MB doc limit and http can't render on the https app.
    const bad =
      !Array.isArray(next.refImages) ||
      next.refImages.some(
        (u) => typeof u !== "string" || !/^https:\/\//i.test(u),
      );
    if (bad)
      throw new Error("refImages must be an array of hosted https:// URLs");
  }
  return next;
}

/**
 * Pure local merge with the same semantics as the write (deletes + metadata
 * mirrors), so callers can update UI state without a re-read.
 */
export function applyIdentityPatchLocally(
  product: ProductInterface,
  patch: ProductIdentityPatch,
): ProductInterface {
  const next: any = { ...product, updatedAt: Date.now() };
  for (const [key, value] of Object.entries(patch)) {
    if (key === "finish") continue;
    if (value === undefined) delete next[key];
    else next[key] = deepClone(value);
  }
  if ("name" in patch && next.materialData)
    next.materialData = { ...next.materialData, name: patch.name };
  if (patch.finish)
    next.materialData = { ...next.materialData, preset: patch.finish };
  if ("supplier" in patch || "productLink" in patch || "category" in patch) {
    const metadata: any = { ...(product.metadata ?? {}) };
    if ("category" in patch) {
      if (patch.category === undefined) delete metadata.mainCategory;
      else metadata.mainCategory = mainCategoryFor(patch.category);
    }
    if ("supplier" in patch) {
      if (patch.supplier === undefined) delete metadata.brand;
      else metadata.brand = patch.supplier;
    }
    if ("productLink" in patch) {
      if (patch.productLink === undefined) delete metadata.url;
      else metadata.url = patch.productLink;
    }
    next.metadata = metadata;
  }
  return next as ProductInterface;
}

/**
 * Write-through identity edit on the caller's OWN userProducts doc. A key
 * explicitly set to `undefined` DELETES the field (updateDoc merges, so
 * omitting it would keep the old value); a cleared field clears its mirror.
 */
export async function updateUserProductIdentity(
  product: ProductInterface,
  patch: ProductIdentityPatch,
): Promise<void> {
  const uid = getAuth().currentUser?.uid;
  if (!canEditUserProductIdentity(product, uid))
    throw new Error("You can only edit your own products");
  const next = normalizeIdentityPatch(patch);
  const update: Record<string, any> = { updatedAt: Date.now() };
  for (const [key, value] of Object.entries(next)) {
    if (key === "finish") continue;
    update[key] = value === undefined ? deleteField() : deepClone(value);
  }
  if (next.finish) update["materialData.preset"] = next.finish;
  if ("supplier" in next)
    update["metadata.brand"] =
      next.supplier === undefined ? deleteField() : next.supplier;
  if ("productLink" in next)
    update["metadata.url"] =
      next.productLink === undefined ? deleteField() : next.productLink;
  if ("name" in next && product.materialData)
    update["materialData.name"] = next.name;
  if ("category" in next)
    update["metadata.mainCategory"] =
      next.category === undefined
        ? deleteField()
        : mainCategoryFor(next.category);
  await updateDoc(doc(firebaseHelper.db, "userProducts", product.id), update);
}

/** Writes generated PBR map URLs onto the caller's own userProducts doc. */
export async function saveProductMapFiles(
  product: ProductInterface,
  files: { normal_original: string; albedo_original?: string },
): Promise<void> {
  if (!canEditUserProductIdentity(product, getAuth().currentUser?.uid))
    throw new Error("You can only edit your own products");
  const update: Record<string, any> = {
    updatedAt: Date.now(),
    "materialData.files.normal_original": files.normal_original,
  };
  if (files.albedo_original)
    update["materialData.files.albedo_original"] = files.albedo_original;
  await updateDoc(doc(firebaseHelper.db, "userProducts", product.id), update);
}
